/**
 * Compulsory add-ons (variant metafield custom.compulsory_add_on).
 * Syncs checkbox UI + configurator-add-ons cart state (production uses selectedAddOns on submit).
 */
(() => {
  function getVariantMetafields() {
    const node = document.getElementById('variant-metafields-data');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (e) {
      console.error('[CompulsoryAddOn] Failed to parse variant metafields', e);
      return {};
    }
  }

  function getVariantsData() {
    const root = document.querySelector('ee-configurator-product-info');
    const variantsScript =
      root?.querySelector('ee-variant-selects script[data-product-variants]') ||
      document.getElementById('ProductJSON-' + (root?.dataset.section || ''));
    if (!variantsScript) return [];
    try {
      const parsed = JSON.parse(variantsScript.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  /** Match metafield id to add-on card input (variant id, product id, or data-compulsory-add-on). */
  function resolveAddOnInput(addOnId) {
    if (addOnId == null || addOnId === '') return null;
    const id = String(addOnId).trim();

    const selectors = [
      `configurator-add-ons input[data-add-on-id="${id}"]`,
      `configurator-add-ons input[data-variant-id="${id}"]`,
      `configurator-add-ons input[data-compulsory-add-on="${id}"]`,
      `configurator-add-ons input[data-product-id="${id}"]`,
      `configurator-add-ons input[id="configurator-${id}"]`,
    ];

    for (const sel of selectors) {
      const input = document.querySelector(sel);
      if (input) return input;
    }

    return null;
  }

  function getConfiguratorAddOnsComponent() {
    return document.querySelector('configurator-add-ons');
  }

  class EEConfiguratorCompulsory {
    constructor() {
      this.variantMetafields = getVariantMetafields();
      this.variantsData = getVariantsData();
      this.currentCompulsoryAddOn = null;
      this.pendingVariantId = null;
      this.boundPreventUncheck = this.boundPreventUncheck.bind(this);
      this.init();
    }

    init() {
      document.addEventListener('change', this.boundPreventUncheck, true);
      document.addEventListener('click', this.boundPreventUncheck, true);

      document.addEventListener('configurator:variant-changed', (e) => {
        const variant = e.detail?.variant;
        if (variant?.id) {
          this.applyForVariant(String(variant.id));
        }
      });

      document.addEventListener('configurator-addons-ready', () => {
        this.flushPendingOrRefresh();
      });

      this.bindVariantSelectListeners();

      // product-info may dispatch variant-changed before this script runs
      this.scheduleInitialApply();
    }

    scheduleInitialApply() {
      this.applyForCurrentContext();
      requestAnimationFrame(() => this.applyForCurrentContext());
    }

    flushPendingOrRefresh() {
      if (this.pendingVariantId) {
        const vid = this.pendingVariantId;
        this.pendingVariantId = null;
        this.applyForVariant(vid);
        return;
      }
      if (this.currentCompulsoryAddOn) {
        this.refreshCurrentUI();
      } else {
        this.applyForCurrentContext();
      }
    }

    bindVariantSelectListeners() {
      document.querySelectorAll('ee-variant-selects').forEach((el) => {
        el.addEventListener('ee-variant-change', (e) => {
          const vid =
            e.detail?.variantId ||
            e.detail?.variant?.id ||
            this.resolveVariantFromSelectedOptions();
          if (vid) this.applyForVariant(String(vid));
        });

        el.addEventListener('change', (e) => {
          const input = e.target;
          if (!input?.matches?.('input[type="radio"]')) return;
          if (input.closest('configurator-add-ons')) return;

          const vid =
            input.getAttribute('data-variant-id') ||
            this.resolveVariantFromSelectedOptions();
          if (vid) {
            setTimeout(() => this.applyForVariant(String(vid)), 0);
          }
        });
      });
    }

    applyForCurrentContext() {
      const info = document.querySelector('ee-configurator-product-info');
      if (info?.currentVariant?.id) {
        this.applyForVariant(String(info.currentVariant.id));
        return;
      }

      const fromOptions = this.resolveVariantFromSelectedOptions();
      if (fromOptions) {
        this.applyForVariant(fromOptions);
        return;
      }

      const initial = this.getInitialVariantId();
      if (initial) this.applyForVariant(initial);
    }

    applyForVariant(variantToken) {
      if (!variantToken) return;
      this.handleCompulsoryAddOn(variantToken);
    }

    getInitialVariantId() {
      const info = document.querySelector('ee-configurator-product-info');
      if (info?.currentVariant?.id) return String(info.currentVariant.id);

      const form = document.querySelector('ee-product-form input[name="id"]');
      if (form?.value) return String(form.value);

      const params = new URLSearchParams(window.location.search);
      if (params.get('variant')) return params.get('variant');

      return this.resolveVariantFromSelectedOptions();
    }

    resolveVariantFromSelectedOptions() {
      const selects = document.querySelector('ee-variant-selects');
      if (!selects || !this.variantsData.length) return null;

      const selected = [];
      selects.querySelectorAll('fieldset').forEach((fs) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        if (checked?.value != null) selected.push(String(checked.value));
      });

      if (!selected.length) return null;

      const match = this.variantsData.find((variant) => {
        if (!Array.isArray(variant.options)) return false;
        if (variant.options.length !== selected.length) return false;
        return variant.options.every(
          (opt, i) => String(opt) === String(selected[i])
        );
      });

      return match?.id ? String(match.id) : null;
    }

    boundPreventUncheck(event) {
      if (!this.currentCompulsoryAddOn) return;
      const input =
        event.target?.matches?.('input[type="checkbox"]') &&
        event.target.closest('configurator-add-ons [data-add-on-id]');
      if (!input) return;

      const addOnId = input.getAttribute?.('data-add-on-id');
      if (addOnId !== this.currentCompulsoryAddOn) return;

      if (event.type === 'click' && input.checked) {
        event.preventDefault();
        event.stopPropagation();
        this.refreshCurrentUI();
        return;
      }
      if (event.type === 'change') {
        input.checked = true;
        event.preventDefault();
        this.refreshCurrentUI();
      }
    }

    handleCompulsoryAddOn(variantToken) {
      if (!variantToken) return;

      const variantId = this.resolveVariantId(variantToken);
      if (!variantId) return;

      const addOnsComponent = getConfiguratorAddOnsComponent();
      if (!addOnsComponent?.initialized) {
        this.pendingVariantId = variantId;
      }

      if (this.currentCompulsoryAddOn) {
        this.unselectAddOn(this.currentCompulsoryAddOn);
        this.currentCompulsoryAddOn = null;
      }

      const meta = this.variantMetafields[variantId]?.custom?.compulsory_add_on;
      if (!meta) return;

      const addOnId = String(meta);
      const input = resolveAddOnInput(addOnId);
      if (!input) return;

      this.currentCompulsoryAddOn = addOnId;
      this.selectAddOn(input, addOnId);
    }

    resolveVariantId(variantToken) {
      const token = String(variantToken);
      const byId = this.variantsData.find((v) => String(v.id) === token);
      if (byId) return String(byId.id);

      const byOption = this.variantsData.find(
        (v) =>
          Array.isArray(v.options) &&
          v.options.some((o) => String(o) === token)
      );
      if (byOption) return String(byOption.id);

      return token;
    }

    getCartVariantId(input, addOnId) {
      return (
        input?.getAttribute('data-variant-id') ||
        input?.dataset?.variantId ||
        input?.getAttribute('data-add-on-id') ||
        addOnId
      );
    }

    applyAddOnUI(input) {
      if (!input) return;

      const container = input.closest('.config-card-container');
      const wrapper = input.closest('.add-on-wrapper-flex');

      input.checked = true;
      input.setAttribute('data-locked', 'true');

      wrapper?.classList.add('compulsory-add-on', 'compulsory-locked');
      wrapper?.classList.remove('selected', 'add-border-3px');
      container?.classList.add('compulsory-add-on');
      getConfiguratorAddOnsComponent()?.setAddOnCardSelected?.(container, true);
    }

    clearAddOnUI(input) {
      if (!input) return;

      const container = input.closest('.config-card-container');
      const wrapper = input.closest('.add-on-wrapper-flex');

      input.checked = false;
      input.removeAttribute('data-locked');

      wrapper?.classList.remove('compulsory-add-on', 'compulsory-locked', 'selected', 'add-border-3px');
      container?.classList.remove('compulsory-add-on');
      getConfiguratorAddOnsComponent()?.setAddOnCardSelected?.(container, false);
    }

    refreshCurrentUI() {
      if (!this.currentCompulsoryAddOn) return;
      const input = resolveAddOnInput(this.currentCompulsoryAddOn);
      if (input) this.applyAddOnUI(input);
    }

    syncToConfiguratorAddOns(addOnId) {
      const input = resolveAddOnInput(addOnId);
      if (!input) return;

      const component = getConfiguratorAddOnsComponent();
      const variantId = this.getCartVariantId(input, addOnId);
      const productId =
        input.dataset.productId ||
        input.getAttribute('data-product-id') ||
        input.value;

      if (component?.initialized) {
        component.selectedAddOns.set(String(variantId), {
          productId,
          variantId: String(variantId),
          input,
        });
        component.updateFormWithAddOns?.();
      }

      try {
        document.dispatchEvent(new Event('configurator:addon-layer-change'));
        document.dispatchEvent(
          new CustomEvent('addon-selection-changed', { bubbles: true })
        );
      } catch (_) {}
    }

    unselectAddOn(addOnId) {
      const input = resolveAddOnInput(addOnId);
      if (input) this.clearAddOnUI(input);

      const component = getConfiguratorAddOnsComponent();
      if (component && input) {
        const variantId = this.getCartVariantId(input, addOnId);
        component.selectedAddOns.delete(String(variantId));
        if (component.initialized) component.updateFormWithAddOns?.();
      }
    }

    selectAddOn(input, addOnId) {
      this.applyAddOnUI(input);
      this.syncToConfiguratorAddOns(addOnId);

      requestAnimationFrame(() => {
        if (this.currentCompulsoryAddOn !== addOnId) return;
        this.applyAddOnUI(input);
        this.syncToConfiguratorAddOns(addOnId);
      });
    }
  }

  function boot() {
    if (!document.getElementById('variant-metafields-data')) return;
    if (window.eeConfiguratorCompulsory) return;
    window.eeConfiguratorCompulsory = new EEConfiguratorCompulsory();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
